import React, { useMemo, useState } from 'react';
import { LuChevronLeft, LuChevronRight, LuFileText, LuMessageSquareText } from 'react-icons/lu';
import { channelLabel, sourceLabel } from '../../../utils/sources.js';
import { cx } from '../../../utils/cx.js';
import { Card, CardBody, CardHeader } from '../../ui/Card';
import Alert from '../../ui/Alert';
import Badge from '../../ui/Badge';
import Button, { FOCUS_RING } from '../../ui/Button';
import IconButton from '../../ui/IconButton';
import Thumb from './Thumb';
import VisitBlock from './VisitBlock';
import VisitTopicsPromptsModal from './VisitTopicsPromptsModal';
import { formatDateTime } from '../../../utils/weekFormat.js';
import { plural } from '../../../utils/text.js';
import { groupByVisit } from './postList.js';
import { parseVersion, versionLabel } from './topicsVersion.js';

const PICTURES_SHOWN = 12;

const PostDetail = ({ fetchWithAuth, post, position, onPrevious, onNext }) => {
  const [allPictures, setAllPictures] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const visits = useMemo(() => groupByVisit(post.rows), [post.rows]);
  const pictures = allPictures ? post.pictures : post.pictures.slice(0, PICTURES_SHOWN);
  const first = post.rows[0];
  const version = parseVersion(first?.topics_version);
  const label = version && versionLabel(version);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          icon={LuMessageSquareText}
          title={`Post du ${formatDateTime(post.started_at)}`}
          subtitle={`${sourceLabel(post.source)} · ${channelLabel(post.channel)} · ${plural(post.picture_count, 'photo')}`}
          actions={(
            <div className="flex items-center gap-1">
              {position && <span className="mr-1 text-xs text-gray-400 dark:text-gray-500">{position}</span>}
              <IconButton variant="outline" icon={LuChevronLeft} label="Post précédent" onClick={onPrevious} disabled={!onPrevious} />
              <IconButton variant="outline" icon={LuChevronRight} label="Post suivant" onClick={onNext} disabled={!onNext} />
            </div>
          )}
        />
        <CardBody className="space-y-4">
          <p className="break-all font-mono text-[11px] text-gray-400 dark:text-gray-500">{post.visit_key}</p>
          <pre className="whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-4 font-sans text-sm leading-relaxed text-gray-800 dark:bg-night-900 dark:text-gray-100">{post.text}</pre>
          {post.pictures.length > 0 && (
            <div>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                {pictures.map((url) => <Thumb key={url} url={url} />)}
              </div>
              {post.pictures.length > PICTURES_SHOWN && (
                <button type="button" onClick={() => setAllPictures((v) => !v)} className={cx('mt-2 rounded text-xs font-medium text-brand-600 hover:underline dark:text-brand-400', FOCUS_RING)}>
                  {allPictures ? 'Réduire' : `Afficher les ${post.pictures.length} photos`}
                </button>
              )}
            </div>
          )}
          {first ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>Analysé le {formatDateTime(first.analysed_at)} · {plural(visits.length, 'visite')}, {plural(post.rows.length, 'ligne')}</span>
              {version ? (
                <>
                  <Badge size="sm" tone="violet" title="Version des instructions de GPT Luna : découpage, rédaction, proposition">{label.prompt}</Badge>
                  <Badge size="sm" tone="violet" title="Version des questions posées à l'API Decisions : topics, autre chose, magasin">{label.questions}</Badge>
                  <Badge size="sm" tone="slate" title="Empreinte de la liste des topics utilisée">Catalogue <span className="font-mono">{version.catalog}</span></Badge>
                </>
              ) : <Badge size="sm" tone="slate"><span className="font-mono">{first.topics_version}</span></Badge>}
              <Button size="sm" variant="ghost" icon={LuFileText} onClick={() => setShowPrompts(true)}>Voir les prompts</Button>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Mis à jour le {formatDateTime(post.updated_at)}</p>
          )}
          {showPrompts && <VisitTopicsPromptsModal fetchWithAuth={fetchWithAuth} version={first.topics_version} onClose={() => setShowPrompts(false)} />}
        </CardBody>
      </Card>
      {visits.length
        ? visits.map((visit) => <VisitBlock key={visit.rank} visit={visit} />)
        : <Alert tone="info" title="Aucune ligne">Le post ne porte pas d'information terrain, ou son analyse a échoué : relancez-le avec --post pour voir le détail.</Alert>}
    </div>
  );
};

export default PostDetail;
